import { formatEther } from 'ethers/lib/utils';
import { useTranslation } from 'next-i18next';
import { VFC } from 'react';

import { useCreation } from '@/apis/aniftyBackend/creations/getCreation';
import { MintAskStatus } from '@/apis/aniftyBackend/mintAsks/getMintAsk';
import { ListingType } from '@/apis/aniftyBackend/mintAsks/interface';
import { CreatedToken } from '@/apis/aniftyBackend/users/interface';
import AuctionCreationCard from '@/components/AuctionCreationCard';
import SaleCreationCard from '@/components/SaleCreationCard';
import routes from '@/config/routes';
import { DEFAULT_PROFILE_IMAGE_PATH } from '@/constants/images';
import ReservedItemOverlay from '@/containers/UserProfilePage/UserItemsSection/ReservedItemOverlay';
import generateSquareImageURL from '@/utils/generateSquareImageURL';
import getThumbnailURL from '@/utils/getThumbnailURL';

import styles from './UsersItem.module.scss';

type Props = {
  item: CreatedToken;
  isMyItem: boolean;
  userName: string;
};

const CreatedUserItem: VFC<Props> = ({ item, isMyItem, userName }: Props) => {
  const { i18n } = useTranslation();

  const creatorName =
    item.creatorProfile.displayNameJP && i18n.language === 'ja'
      ? item.creatorProfile.displayNameJP
      : item.creatorProfile.displayNameEN;

  const creatorImage = !item.creatorProfile.profilePicUrl
    ? DEFAULT_PROFILE_IMAGE_PATH
    : generateSquareImageURL(item.creatorProfile.profilePicUrl, 330);

  const price = item.price ? formatEther(item.price) : null;

  const tokenLink =
    item.aniftyTokenID === 85
      ? 'https://opensea.io/assets/0x21545e9f21844758f6d4755230d9d7c62e08b449/85'
      : routes.creationDetail(item.creationID.toString());

  const tokenName =
    item.tokenNameJA && i18n.language === 'ja'
      ? item.tokenNameJA
      : item.tokenName;

  const { data: creation } = useCreation({ creationID: item.creationID });

  return (
    <div
      className={item.reservedAt ? styles.reservedUsersItem : styles.usersItem}
    >
      {item.reservedAt && (
        <ReservedItemOverlay
          creation={{
            ...item,
            creationID: item.creationID || 0,
            width: item.width || 0,
            height: item.height || 0,
            cropStartX: item.cropStartX || 0,
            cropStartY: item.cropStartY || 0,
            cropSize: item.cropSize || 0,

            mintAsk: null,
            reservedAt: item.reservedAt,
            title: item.tokenName,
            titleJA: item.tokenNameJA,
            description: '',
            descriptionJA: null,
            creator: {
              userID: '',
              displayName: item.creatorProfile.displayNameEN,
              displayNameJA: item.creatorProfile.displayNameJP,
              profileImageURL: item.creatorProfile.profilePicUrl,
              userName: '',
            },
            tags: [],
            ipfsImageUri: null,
            ipfsMetadataUri: null,
            createdAt: '',
            mintBids: [],
            likeSummary: { likeCount: 0 },
          }}
          listingData={{
            supply: item.totalTokenAmount,
            price: Number(formatEther(item.price)),
          }}
          reservedAt={item.reservedAt}
          isMyItem={isMyItem}
          creatorUserName={userName}
          listingType={item.listingType}
        />
      )}

      {item.listingType === ListingType.Sale ? (
        <SaleCreationCard
          link={tokenLink}
          creationImageUrl={item.imageURL}
          thumbnailUrl={getThumbnailURL(item.imageURL, {
            width: item.width,
            height: item.height,
            cropStartX: item.cropStartX,
            cropStartY: item.cropStartY,
            cropSize: item.cropSize,
          })}
          creationName={tokenName}
          creatorName={creatorName}
          creatorProfileImageUrl={creatorImage}
          price={price}
          availableTokenAmount={item.availableTokenAmount}
          totalTokenAmount={item.totalTokenAmount}
        />
      ) : (
        <AuctionCreationCard
          link={tokenLink}
          creationImageUrl={item.imageURL}
          thumbnailUrl={getThumbnailURL(item.imageURL, {
            width: item.width,
            height: item.height,
            cropStartX: item.cropStartX,
            cropStartY: item.cropStartY,
            cropSize: item.cropSize,
          })}
          creationName={tokenName}
          creatorName={creatorName}
          creatorProfileImageUrl={creatorImage}
          reservedPrice={price}
          highestBidPrice={
            item.highestBidPrice && formatEther(item.highestBidPrice)
          }
          endTime={item.endTime}
          status={
            creation?.mintAsk ? creation.mintAsk.status : MintAskStatus.Ongoing
          }
        />
      )}
    </div>
  );
};

export default CreatedUserItem;
